import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Card, 
  Box, 
  Typography, 
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Chip
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import moment from 'moment';
import SecureLS from 'secure-ls';
import user_api from '../../../api/user';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import clockFill from '@iconify/icons-eva/clock-fill';
import calendarFill from '@iconify/icons-eva/calendar-fill';
import pinFill from '@iconify/icons-eva/pin-fill';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-fill';
import alertCircleFill from '@iconify/icons-eva/alert-circle-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import warningFill from '@iconify/icons-eva/alert-triangle-fill';

const ls = new SecureLS({ encodingType: "aes" });

// Enhanced Payment Status Constants
const PAYMENT_STATUSES = {
  PENDING: 'pending',
  VERIFIED: 'verified',
  PAID: 'paid',
  REFUND_PENDING: 'refund_pending',
  PARTIALLY_REFUNDED: 'partially_refunded',
  FULLY_REFUNDED: 'fully_refunded',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected'
};

// Booking Status Constants
const BOOKING_STATUSES = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  CANCELLATION_REQUESTED: 'cancellation_requested'
};

// Styled components
const RootStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  background: 'linear-gradient(to bottom right, #0f172a, #1e293b)',
  color: '#ffffff',
  paddingBottom: theme.spacing(10),
  paddingTop: theme.spacing(15)
}));

const CardStyle = styled(Card)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(100, 116, 139, 0.2)',
  padding: theme.spacing(4),
  boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.1)',
  color: '#ffffff'
}));

const BookingCardStyle = styled(Card)(({ theme, status }) => ({
  background: 'rgba(15, 23, 42, 0.9)',
  backdropFilter: 'blur(8px)',
  border: `1px solid ${
    status === 'confirmed' 
      ? 'rgba(6, 182, 212, 0.5)' 
      : status === 'pending' 
        ? 'rgba(255, 215, 0, 0.5)' 
        : status === 'completed'
          ? 'rgba(76, 175, 80, 0.5)'
          : 'rgba(255, 68, 68, 0.5)'
  }`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.1)',
  color: '#ffffff',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px 0 rgba(0, 0, 0, 0.2)'
  }
}));

const TabsStyle = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: 'rgb(6, 182, 212)',
  },
  '& .MuiTab-root': {
    color: theme.palette.grey[500],
    '&.Mui-selected': {
      color: 'rgb(6, 182, 212)',
    }
  }
}));

const CancelDialogStyle = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    border: '1px solid #374151',
    minWidth: '500px',
    maxWidth: '600px'
  }
}));

export default function MyBookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [openAlert, setOpenAlert] = useState(false);
  
  // Enhanced cancel dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelType, setCancelType] = useState('emergency');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  
  // New state variables to track booking types
  const [hasCompletedBookings, setHasCompletedBookings] = useState(false);
  const [hasUpcomingBookings, setHasUpcomingBookings] = useState(false);
  const [hasPendingBookings, setHasPendingBookings] = useState(false);

  const convertTo12Hour = (timeString) => {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Enhanced payment status determination
  const getEnhancedPaymentStatus = (booking) => {
    // Check if booking is cancelled
    if (booking.bookingStatus === BOOKING_STATUSES.CANCELLED) {
      // Check if there's a cancellation request with refund info
      if (booking.refundStatus) {
        switch (booking.refundStatus) {
          case 'pending':
            return PAYMENT_STATUSES.REFUND_PENDING;
          case 'completed':
            return PAYMENT_STATUSES.FULLY_REFUNDED;
          case 'rejected':
            // If refund was rejected, check original payment status
            const originalStatus = booking.paymentStatus || booking.payment_status;
            return originalStatus === 'verified' || originalStatus === 'paid' 
              ? PAYMENT_STATUSES.PAID 
              : PAYMENT_STATUSES.PENDING;
          case 'not_applicable':
            // No refund needed (e.g., no payment was made)
            return PAYMENT_STATUSES.CANCELLED;
          default:
            // Default for cancelled bookings - assume waiting for refund if payment was made
            const cancelStatus = booking.paymentStatus || booking.payment_status;
            return originalStatus === 'verified' || originalStatus === 'paid' 
              ? PAYMENT_STATUSES.REFUND_PENDING 
              : PAYMENT_STATUSES.CANCELLED;
        }
      }
      
      // No refund info available, check if payment was made
      const originalStatus = booking.paymentStatus || booking.payment_status;
      if (originalStatus === 'verified' || originalStatus === 'paid') {
        // Payment was made but no refund info - assume waiting for refund
        return PAYMENT_STATUSES.REFUND_PENDING;
      } else {
        // No payment was made, so just cancelled
        return PAYMENT_STATUSES.CANCELLED;
      }
    }
    
    // Check if cancellation is requested
    if (booking.bookingStatus === BOOKING_STATUSES.CANCELLATION_REQUESTED) {
      return PAYMENT_STATUSES.REFUND_PENDING;
    }
    
    // Normal payment status logic for active bookings
    const originalStatus = booking.paymentStatus || booking.payment_status;
    
    switch (originalStatus) {
      case 'verified':
      case 'paid':
        return PAYMENT_STATUSES.PAID;
      case 'pending':
        return PAYMENT_STATUSES.PENDING;
      case 'rejected':
        return PAYMENT_STATUSES.REJECTED;
      default:
        return PAYMENT_STATUSES.PENDING;
    }
  };

  // Get payment status display info
  const getPaymentStatusInfo = (status) => {
    switch (status) {
      case PAYMENT_STATUSES.PENDING:
        return {
          label: 'Payment Pending',
          color: '#ffd700',
          bgColor: 'rgba(255, 215, 0, 0.1)',
          icon: '⏳'
        };
      case PAYMENT_STATUSES.PAID:
        return {
          label: 'Paid',
          color: '#4caf50',
          bgColor: 'rgba(76, 175, 80, 0.1)',
          icon: '✅'
        };
      case PAYMENT_STATUSES.REFUND_PENDING:
        return {
          label: 'Waiting for Refund',
          color: '#ff8800',
          bgColor: 'rgba(255, 136, 0, 0.1)',
          icon: '⏳'
        };
      case PAYMENT_STATUSES.FULLY_REFUNDED:
        return {
          label: 'Refunded',
          color: '#06b6d4',
          bgColor: 'rgba(6, 182, 212, 0.1)',
          icon: '💰'
        };
      case PAYMENT_STATUSES.PARTIALLY_REFUNDED:
        return {
          label: 'Partially Refunded',
          color: '#8b5cf6',
          bgColor: 'rgba(139, 92, 246, 0.1)',
          icon: '💰'
        };
      case PAYMENT_STATUSES.CANCELLED:
        return {
          label: 'Cancelled (No Refund)',
          color: '#6b7280',
          bgColor: 'rgba(107, 114, 128, 0.1)',
          icon: '🚫'
        };
      case PAYMENT_STATUSES.REJECTED:
        return {
          label: 'Payment Rejected',
          color: '#ef4444',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          icon: '❌'
        };
      default:
        return {
          label: 'Unknown',
          color: '#B3B3B3',
          bgColor: 'rgba(179, 179, 179, 0.1)',
          icon: '❓'
        };
    }
  };

  // Check authentication and fetch bookings
  useEffect(() => {
    const token = ls.get("token");
    
    if (!token) {
      navigate('/');
      return;
    }

    const fetchBookings = async () => {
      try {
        // Try enhanced endpoint first, fall back to regular if not available
        let result;
        try {
          result = await user_api.get_user_bookings(); // This should now use enhanced endpoint
        } catch (enhancedError) {
          console.warn('Enhanced endpoint not available, using regular endpoint:', enhancedError);
          result = await user_api.get_user_bookings();
        }
        
        if (result && result.data) {
          console.log('API Response:', result.data);
          
          let bookingsData = [];
          
          if (result.data.data && Array.isArray(result.data.data)) {
            bookingsData = result.data.data;
          }
          else if (Array.isArray(result.data)) {
            bookingsData = result.data;
          }
          else if (result.data.success && result.data.data) {
            bookingsData = Array.isArray(result.data.data) ? result.data.data : [];
          }
          
          const transformedBookings = bookingsData.map(booking => {
            const reservationDate = booking.reservation_date || booking.reservationDate;
            const isPastBooking = moment(reservationDate).isBefore(moment(), 'day');
            
            let bookingStatus = booking.status || booking.bookingStatus;
            if (bookingStatus === 'confirmed' && isPastBooking) {
              bookingStatus = 'completed';
            }
            
            // Enhanced booking object with cancellation and refund info
            const enhancedBooking = {
              id: booking.id,
              bookingRef: booking.booking_id || booking.bookingRef,
              courtName: booking.court_name || booking.courtName || 'Unknown Court',
              courtLocation: booking.court_location || booking.courtLocation || 'Location not specified',
              reservationDate: reservationDate,
              timeFrom: booking.time_from || booking.timeFrom,
              timeTo: booking.time_to || booking.timeTo,
              totalHours: booking.total_hours || booking.totalHours,
              totalAmount: booking.total_amount || booking.totalAmount,
              downpaymentAmount: booking.downpayment_amount || booking.downpaymentAmount,
              bookingStatus: bookingStatus,
              paymentStatus: booking.payment_status || booking.paymentStatus || 'pending',
              // Add cancellation and refund tracking from enhanced API
              cancellationStatus: booking.cancellation_status,
              refundStatus: booking.refund_status,
              refundAmount: booking.refund_amount,
              cancelledAt: booking.cancelled_at,
              refundProcessedAt: booking.refund_processed_at || booking.refund_processed_at,
              hasCancellationRequest: booking.has_cancellation_request,
              cancellationInfo: booking.cancellation_info
            };
            
            return enhancedBooking;
          });
          
          setBookings(transformedBookings);
          
          const completed = transformedBookings.some(booking => 
            booking.bookingStatus === 'completed' || 
            (booking.bookingStatus === 'confirmed' && moment(booking.reservationDate).isBefore(moment(), 'day'))
          );
          
          const upcoming = transformedBookings.some(booking => 
            (booking.bookingStatus === 'confirmed' || booking.bookingStatus === 'pending') && 
            moment(booking.reservationDate).isAfter(moment(), 'day')
          );
          
          const pending = transformedBookings.some(booking => 
            booking.bookingStatus === 'pending'
          );
          
          setHasCompletedBookings(completed);
          setHasUpcomingBookings(upcoming);
          setHasPendingBookings(pending);
          
          let defaultTab = 0;
          
          if (upcoming) {
            defaultTab = 1;
          } else if (pending) {
            defaultTab = 3;
          } else if (completed) {
            defaultTab = 4;
          }
          
          setActiveTab(defaultTab);
          filterBookingsByTab(defaultTab, transformedBookings);
        } else {
          showAlert('Failed to load bookings', 'error');
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        showAlert('Error loading bookings. Please try again.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, [navigate]);

  const filterBookingsByTab = (tabIndex, bookingsData = bookings) => {
    if (!Array.isArray(bookingsData)) {
      setFilteredBookings([]);
      return;
    }
    
    const today = moment().startOf('day');

    switch (tabIndex) {
      case 0: // All
        setFilteredBookings(bookingsData);
        break;
      case 1: // Upcoming
        setFilteredBookings(bookingsData.filter(booking => 
          (booking.bookingStatus === 'confirmed' || booking.bookingStatus === 'pending') && 
          moment(booking.reservationDate).startOf('day').isSameOrAfter(today)
        ));
        break;
      case 2: // Confirmed
        setFilteredBookings(bookingsData.filter(booking => 
          booking.bookingStatus === 'confirmed'
        ));
        break;
      case 3: // Pending
        setFilteredBookings(bookingsData.filter(booking => 
          booking.bookingStatus === 'pending'
        ));
        break;
      case 4: // Completed
        setFilteredBookings(bookingsData.filter(booking => 
          booking.bookingStatus === 'completed' || 
          (booking.bookingStatus === 'confirmed' && moment(booking.reservationDate).startOf('day').isBefore(today))
        ));
        break;
      case 5: // Cancelled
        setFilteredBookings(bookingsData.filter(booking => 
          booking.bookingStatus === 'cancelled'
        ));
        break;
      default:
        setFilteredBookings(bookingsData);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    filterBookingsByTab(newValue);
  };

  const showAlert = (message, severity = 'success') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setCancelConfirmText('');
    setCancelReason('');
    setCancelType('emergency');
    setAdditionalNotes('');
    setCancelDialogOpen(true);
  };

  const getCancelReason = () => {
    const reasons = {
      emergency: 'Emergency or unforeseen circumstances',
      change_plans: 'Change of plans',
      double_booking: 'Accidental double booking',
      weather: 'Weather conditions',
      other: 'Other reason'
    };
    return reasons[cancelType] || 'Other reason';
  };

  // Updated confirmCancelBooking method with enhanced logic
  const confirmCancelBooking = async () => {
    if (!bookingToCancel) return;

    // Validate confirmation text
    const expectedText = `cancel ${bookingToCancel.bookingRef}`;
    if (cancelConfirmText.toLowerCase() !== expectedText.toLowerCase()) {
      showAlert(`Please type exactly: "${expectedText}" to confirm cancellation`, 'error');
      return;
    }

    if (!cancelReason.trim()) {
      showAlert('Please provide a reason for cancellation', 'error');
      return;
    }

    setCancelSubmitting(true);

    try {
      const cancelData = {
        bookingId: bookingToCancel.id,
        reason: cancelReason,
        cancelType: cancelType,
        additionalNotes: additionalNotes,
        bookingRef: bookingToCancel.bookingRef
      };

      const result = await user_api.request_booking_cancellation(cancelData);
      
      if (result && result.status === 200) {
        const responseData = result.data.data;
        
        // Handle different types of cancellation responses
        if (responseData.requiresApproval === false) {
          // Immediate cancellation
          const updatedBookings = bookings.map(b => 
            b.id === bookingToCancel.id 
              ? { 
                  ...b, 
                  bookingStatus: 'cancelled',
                  cancellationStatus: 'auto_approved',
                  refundStatus: responseData.refundAmount > 0 ? 'pending' : 'not_applicable',
                  refundAmount: responseData.refundAmount
                } 
              : b
          );
          
          setBookings(updatedBookings);
          filterBookingsByTab(activeTab, updatedBookings);
          
          if (responseData.refundAmount > 0) {
            showAlert(
              `✅ Booking cancelled successfully! A refund of ₱${responseData.refundAmount.toLocaleString()} will be processed within 3-5 business days. You will receive a confirmation email shortly.`, 
              'success'
            );
          } else {
            showAlert(
              '✅ Booking cancelled successfully! You will receive a confirmation email shortly.', 
              'success'
            );
          }
        } else {
          // Traditional approval flow
          const updatedBookings = bookings.map(b => 
            b.id === bookingToCancel.id 
              ? { 
                  ...b, 
                  bookingStatus: 'cancellation_requested',
                  cancellationStatus: 'pending_approval'
                } 
              : b
          );
          
          setBookings(updatedBookings);
          filterBookingsByTab(activeTab, updatedBookings);
          
          showAlert(
            '📋 Cancellation request submitted successfully. Our team will review your request and you will receive an email confirmation.', 
            'info'
          );
        }
        
        setCancelDialogOpen(false);
      } else {
        showAlert(result?.data?.message || 'Failed to submit cancellation request', 'error');
      }
    } catch (error) {
      console.error('Error requesting booking cancellation:', error);
      showAlert('Error submitting cancellation request. Please try again.', 'error');
    } finally {
      setCancelSubmitting(false);
      setBookingToCancel(null);
    }
  };

  // Enhanced cancellation policy information
  const CancellationPolicyInfo = ({ booking }) => {
    const reservationDateTime = new Date(`${booking.reservationDate}T${booking.timeFrom}`);
    const now = new Date();
    const hoursUntilBooking = (reservationDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    let policyInfo = {
      type: 'approval',
      refundAmount: 'Admin will decide',
      processingTime: '24-48 hours for approval',
      color: '#f59e0b',
      icon: '⏳'
    };
    
    const paymentStatus = getEnhancedPaymentStatus(booking);
    
    if (paymentStatus === PAYMENT_STATUSES.PENDING) {
      policyInfo = {
        type: 'immediate',
        refundAmount: booking.downpaymentAmount ? `₱${booking.downpaymentAmount.toLocaleString()}` : 'No payment made',
        processingTime: 'Immediate cancellation',
        color: '#22c55e',
        icon: '⚡'
      };
    } else if (paymentStatus === PAYMENT_STATUSES.PAID && hoursUntilBooking > 24) {
      policyInfo = {
        type: 'immediate',
        refundAmount: `₱${booking.downpaymentAmount?.toLocaleString()} (Full refund)`,
        processingTime: 'Immediate cancellation',
        color: '#22c55e',
        icon: '⚡'
      };
    } else if (paymentStatus === PAYMENT_STATUSES.PAID && hoursUntilBooking > 2) {
      policyInfo = {
        type: 'immediate',
        refundAmount: `₱${(booking.downpaymentAmount * 0.5)?.toLocaleString()} (50% refund)`,
        processingTime: 'Immediate cancellation',
        color: '#f59e0b',
        icon: '⚡'
      };
    }
    
    return (
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        bgcolor: `rgba(${policyInfo.color === '#22c55e' ? '34, 197, 94' : policyInfo.color === '#f59e0b' ? '245, 158, 11' : '239, 68, 68'}, 0.1)`, 
        borderRadius: 1, 
        border: `1px solid rgba(${policyInfo.color === '#22c55e' ? '34, 197, 94' : policyInfo.color === '#f59e0b' ? '245, 158, 11' : '239, 68, 68'}, 0.3)` 
      }}>
        <Typography variant="h6" sx={{ color: policyInfo.color, mb: 1, fontWeight: 'bold' }}>
          {policyInfo.icon} Cancellation Policy
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
              <strong>Refund Amount:</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: policyInfo.color, fontWeight: 'bold' }}>
              {policyInfo.refundAmount}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
              <strong>Processing Time:</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: policyInfo.color, fontWeight: 'bold' }}>
              {policyInfo.processingTime}
            </Typography>
          </Grid>
        </Grid>
        
        {policyInfo.type === 'immediate' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(6, 182, 212, 0.1)', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              ✨ Good News: This cancellation will be processed immediately!
            </Typography>
          </Box>
        )}
        
        {policyInfo.type === 'approval' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(245, 158, 11, 0.1)', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
              ⏳ This cancellation requires admin approval due to timing or policy.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <Icon icon={checkmarkCircleFill} width={20} height={20} style={{ color: 'rgb(6, 182, 212)' }} />;
      case 'pending':
        return <Icon icon={alertCircleFill} width={20} height={20} style={{ color: '#ffd700' }} />;
      case 'cancelled':
        return <Icon icon={closeCircleFill} width={20} height={20} style={{ color: '#ff4444' }} />;
      case 'cancellation_requested':
        return <Icon icon={warningFill} width={20} height={20} style={{ color: '#ff8800' }} />;
      case 'completed':
        return <Icon icon={checkmarkCircleFill} width={20} height={20} style={{ color: '#4caf50' }} />;
      default:
        return <Icon icon={alertCircleFill} width={20} height={20} style={{ color: '#B3B3B3' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'rgb(6, 182, 212)';
      case 'pending':
        return '#ffd700';
      case 'cancelled':
        return '#ff4444';
      case 'cancellation_requested':
        return '#ff8800';
      case 'completed':
        return '#4caf50';
      default:
        return '#B3B3B3';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'cancellation_requested':
        return 'Cancellation Requested';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <RootStyle>
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress sx={{ color: 'rgb(6, 182, 212)' }} />
          </Box>
        </Container>
      </RootStyle>
    );
  }

  return (
    <RootStyle>
      <Container>
        <Typography variant="h3" sx={{ mb: 5, color: 'rgb(6, 182, 212)', textAlign: 'center' }}>
          My Bookings
        </Typography>
        
        <CardStyle>
          <TabsStyle value={activeTab} onChange={handleTabChange}>
            <Tab label="All" />
            <Tab label="Upcoming" />
            <Tab label="Confirmed" />
            <Tab label="Pending" />
            <Tab label="Completed" />
            <Tab label="Cancelled" />
          </TabsStyle>

          {!Array.isArray(filteredBookings) || filteredBookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography variant="h6" sx={{ color: '#B3B3B3' }}>
                No bookings found
              </Typography>
            </Box>
          ) : (
            filteredBookings.map((booking) => {
              const enhancedPaymentStatus = getEnhancedPaymentStatus(booking);
              const paymentStatusInfo = getPaymentStatusInfo(enhancedPaymentStatus);
              
              return (
                <BookingCardStyle key={booking.id} status={booking.bookingStatus}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" sx={{ color: 'white', mr: 2 }}>
                          {booking.courtName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                          {getStatusIcon(booking.bookingStatus)}
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              ml: 1,
                              color: getStatusColor(booking.bookingStatus),
                              textTransform: 'capitalize'
                            }}
                          >
                            {getStatusText(booking.bookingStatus)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Icon icon={pinFill} width={16} height={16} style={{ color: '#B3B3B3', marginRight: 8 }} />
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {booking.courtLocation}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Icon icon={calendarFill} width={16} height={16} style={{ color: '#B3B3B3', marginRight: 8 }} />
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {moment(booking.reservationDate).format('MMMM D, YYYY')}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Icon icon={clockFill} width={16} height={16} style={{ color: '#B3B3B3', marginRight: 8 }} />
                        <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                          {convertTo12Hour(booking.timeFrom)} - {convertTo12Hour(booking.timeTo)} ({booking.totalHours} hours)
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'rgb(6, 182, 212)', mb: 1 }}>
                          Booking Reference
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {booking.bookingRef}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      {/* Enhanced Payment Status */}
                      <Box sx={{ 
                        bgcolor: paymentStatusInfo.bgColor, 
                        p: 2, 
                        borderRadius: 1, 
                        mb: 2,
                        border: `1px solid ${paymentStatusInfo.color}40`
                      }}>
                        <Typography variant="subtitle2" sx={{ color: 'rgb(6, 182, 212)', mb: 1 }}>
                          Payment Status
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span style={{ fontSize: '16px' }}>{paymentStatusInfo.icon}</span>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: paymentStatusInfo.color,
                              fontWeight: 'bold'
                            }}
                          >
                            {paymentStatusInfo.label}
                          </Typography>
                        </Box>
                        
                        {/* Show refund info if applicable */}
                        {booking.refundAmount && (
                          <Typography variant="caption" sx={{ color: '#a0aec0', display: 'block', mt: 1 }}>
                            Refund: ₱{parseFloat(booking.refundAmount).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ p: 2, borderRadius: 1, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'rgb(6, 182, 212)', mb: 1 }}>
                          Amount Details
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>Total:</Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            ₱{booking.totalAmount ? booking.totalAmount.toLocaleString() : '0'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>Downpayment:</Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            ₱{booking.downpaymentAmount ? booking.downpaymentAmount.toLocaleString() : '0'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>Balance:</Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            ₱{(booking.totalAmount && booking.downpaymentAmount) 
                                ? (booking.totalAmount - booking.downpaymentAmount).toLocaleString() 
                                : '0'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Action buttons */}
                      {(booking.bookingStatus !== 'cancelled' && 
                        booking.bookingStatus !== 'cancellation_requested' &&
                        booking.bookingStatus !== 'completed' &&
                        moment(booking.reservationDate).isAfter(moment(), 'day')) ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          onClick={() => handleCancelBooking(booking)}
                          sx={{
                            color: '#ff4444',
                            borderColor: '#ff4444',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 68, 68, 0.1)',
                              borderColor: '#ff4444'
                            }
                          }}
                        >
                          Request Cancellation
                        </Button>
                      ) : booking.bookingStatus === 'cancellation_requested' ? (
                        <Button
                          fullWidth
                          variant="outlined"
                          disabled
                          sx={{
                            borderColor: '#ff8800'
                          }}
                        >
                          Cancellation Pending
                        </Button>
                      ) : null}
                    </Grid>
                  </Grid>
                </BookingCardStyle>
              );
            })
          )}
        </CardStyle>
      </Container>
      
      {/* Enhanced Cancel Booking Dialog */}
      <CancelDialogStyle
        open={cancelDialogOpen}
        onClose={() => !cancelSubmitting && setCancelDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #374151', 
          display: 'flex', 
          alignItems: 'center',
          color: bookingToCancel?.paymentStatus === 'pending' || 
                 (bookingToCancel && moment(bookingToCancel.reservationDate).diff(moment(), 'hours') > 2) 
                 ? '#22c55e' : '#f59e0b'
        }}>
          <Icon icon={warningFill} width={24} height={24} style={{ marginRight: 8 }} />
          {bookingToCancel && (
            bookingToCancel.paymentStatus === 'pending' || 
            moment(bookingToCancel.reservationDate).diff(moment(), 'hours') > 2
              ? "⚡ Request Immediate Cancellation"
              : "📋 Request Cancellation Approval"
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {bookingToCancel && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(6, 182, 212, 0.1)', borderRadius: 1, border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                <Typography variant="body2" sx={{ color: '#06b6d4', fontWeight: 'bold', mb: 1 }}>
                  📋 Cancellation Process
                </Typography>
                <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                  {bookingToCancel.paymentStatus === 'pending' || !bookingToCancel.paymentStatus
                    ? "Since no payment has been confirmed yet, this booking can be cancelled immediately without admin approval."
                    : moment(bookingToCancel.reservationDate).diff(moment(), 'hours') > 24
                      ? "Since your booking is more than 24 hours away, it qualifies for immediate cancellation with full refund."
                      : moment(bookingToCancel.reservationDate).diff(moment(), 'hours') > 2
                        ? "Since your booking is between 2-24 hours away, it qualifies for immediate cancellation with 50% refund."
                        : "Your cancellation request will be reviewed by our admin team. You will receive an email notification once processed."
                  }
                </Typography>
              </Box>

              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 68, 68, 0.1)', borderRadius: 1, border: '1px solid rgba(255, 68, 68, 0.3)' }}>
                <Typography variant="h6" sx={{ color: '#ff4444', mb: 1 }}>
                  ⚠️ Important Cancellation Information
                </Typography>
                <Typography variant="body2" sx={{ color: '#e5e7eb', mb: 2 }}>
                  You are about to request cancellation for:
                </Typography>
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {bookingToCancel.courtName} • {bookingToCancel.bookingRef}
                </Typography>
                <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                  {moment(bookingToCancel.reservationDate).format('MMMM D, YYYY')} at {convertTo12Hour(bookingToCancel.timeFrom)}
                </Typography>
                
                {(bookingToCancel.paymentStatus === 'paid' || bookingToCancel.paymentStatus === 'verified') && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(34, 197, 94, 0.1)', borderRadius: 1, border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                    <Typography variant="body2" sx={{ color: '#22c55e' }}>
                      💳 Your payment of ₱{bookingToCancel.downpaymentAmount?.toLocaleString()} will be processed for refund according to our cancellation policy.
                    </Typography>
                  </Box>
                )}
              </Box>

              <CancellationPolicyInfo booking={bookingToCancel} />

              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ color: '#e5e7eb', mb: 2 }}>
                    Reason for Cancellation *
                  </FormLabel>
                  <RadioGroup
                    value={cancelType}
                    onChange={(e) => setCancelType(e.target.value)}
                  >
                    <FormControlLabel 
                      value="emergency" 
                      control={<Radio sx={{ color: '#06b6d4' }} />} 
                      label="Emergency or unforeseen circumstances"
                      sx={{ color: '#e5e7eb' }}
                    />
                    <FormControlLabel 
                      value="change_plans" 
                      control={<Radio sx={{ color: '#06b6d4' }} />} 
                      label="Change of plans"
                      sx={{ color: '#e5e7eb' }}
                    />
                    <FormControlLabel 
                      value="double_booking" 
                      control={<Radio sx={{ color: '#06b6d4' }} />} 
                      label="Accidental double booking"
                      sx={{ color: '#e5e7eb' }}
                    />
                    <FormControlLabel 
                      value="weather" 
                      control={<Radio sx={{ color: '#06b6d4' }} />} 
                      label="Weather conditions"
                      sx={{ color: '#e5e7eb' }}
                    />
                    <FormControlLabel 
                      value="other" 
                      control={<Radio sx={{ color: '#06b6d4' }} />} 
                      label="Other reason"
                      sx={{ color: '#e5e7eb' }}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label="Detailed Reason *"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                multiline
                rows={3}
                placeholder="Please provide a detailed explanation for your cancellation request..."
                disabled={cancelSubmitting}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#4b5563' },
                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                  },
                  '& .MuiInputLabel-root': { color: '#9ca3af' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
                  '& .MuiOutlinedInput-input': { color: '#e5e7eb' }
                }}
              />

              <TextField
                fullWidth
                label="Additional Notes (Optional)"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                multiline
                rows={2}
                placeholder="Any additional information you'd like to provide..."
                disabled={cancelSubmitting}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': { borderColor: '#374151' },
                    '&:hover fieldset': { borderColor: '#4b5563' },
                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                  },
                  '& .MuiInputLabel-root': { color: '#9ca3af' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
                  '& .MuiOutlinedInput-input': { color: '#e5e7eb' }
                }}
              />

              <Divider sx={{ my: 3, borderColor: '#374151' }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#fbbf24', mb: 2, fontWeight: 'bold' }}>
                  ⚠️ Confirmation Required
                </Typography>
                <Typography variant="body2" sx={{ color: '#e5e7eb', mb: 2 }}>
                  To confirm this cancellation request, please type the following exactly:
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.3)', mb: 2 }}>
                  <Typography variant="body1" sx={{ color: '#3b82f6', fontFamily: 'monospace', fontWeight: 'bold' }}>
                    cancel {bookingToCancel.bookingRef}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Type confirmation text here"
                  value={cancelConfirmText}
                  onChange={(e) => setCancelConfirmText(e.target.value)}
                  placeholder={`cancel ${bookingToCancel.bookingRef}`}
                  disabled={cancelSubmitting}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': { borderColor: '#374151' },
                      '&:hover fieldset': { borderColor: '#4b5563' },
                      '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                    },
                    '& .MuiInputLabel-root': { color: '#9ca3af' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
                    '& .MuiOutlinedInput-input': { color: '#e5e7eb', fontFamily: 'monospace' }
                  }}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #374151', gap: 2 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={cancelSubmitting}
            sx={{ color: '#9ca3af' }}
          >
            Keep Booking
          </Button>
          <Button 
            onClick={confirmCancelBooking} 
            disabled={cancelSubmitting || !cancelReason.trim() || cancelConfirmText.toLowerCase() !== `cancel ${bookingToCancel?.bookingRef}`.toLowerCase()}
            variant="contained"
            sx={{
              bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
              '&:disabled': { bgcolor: '#374151', color: '#6b7280' }
            }}
            startIcon={cancelSubmitting ? <CircularProgress size={20} color="inherit" /> : <Icon icon={warningFill} width={20} height={20} />}
          >
            {cancelSubmitting ? 'Submitting Request...' : 'Submit Cancellation Request'}
          </Button>
        </DialogActions>
      </CancelDialogStyle>
      
      {/* Alert Snackbar */}
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={() => setOpenAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenAlert(false)}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </RootStyle>
  );
}